import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Card, Col, Form, Row as BRow } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { FormError, MobileField, TextField, applyServerErrors } from '../forms/fields';
import { mobileOpt, newPasswordReq, req, toText } from '../forms/zod';
import { useToast } from '../ui/feedback';
import { PageHeader } from '../ui/PageHeader';

const detailsSchema = z.object({ name: req(), mobileNo: mobileOpt });
const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: newPasswordReq,
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, { path: ['confirm'], message: 'Passwords do not match' });

function DetailsCard() {
  const { me, refresh } = useAuth();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({ resolver: zodResolver(detailsSchema), defaultValues: { name: me?.user.name ?? '', mobileNo: '' } });
  const save = useMutation({
    mutationFn: (v: z.infer<typeof detailsSchema>) => api.patch('/auth/profile', { name: v.name.trim(), mobileNo: toText(v.mobileNo) }),
    onSuccess: async () => { await refresh(); toast.success('Profile updated'); },
    onError: (e) => setError(applyServerErrors(form as never, e)),
  });
  return (
    <Card className="h-100">
      <Card.Header className="fw-semibold">Your details</Card.Header>
      <Card.Body>
        <Form noValidate onSubmit={form.handleSubmit((v) => { setError(null); save.mutate(v); })}>
          <FormError message={error} />
          <Form.Group className="mb-3" controlId="profile-email"><Form.Label>Email</Form.Label><Form.Control value={me?.user.email ?? ''} readOnly plaintext /></Form.Group>
          <Form.Group className="mb-3" controlId="profile-role"><Form.Label>Role</Form.Label><Form.Control value={me?.user.role ?? ''} readOnly plaintext /></Form.Group>
          <TextField form={form as never} name="name" label="Name" required />
          <MobileField form={form as never} name="mobileNo" label="Mobile" help="Only sent if you change it" />
          <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save details'}</Button>
        </Form>
      </Card.Body>
    </Card>
  );
}

function PasswordCard() {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({ resolver: zodResolver(passwordSchema), defaultValues: { currentPassword: '', newPassword: '', confirm: '' } });
  const change = useMutation({
    mutationFn: (v: z.infer<typeof passwordSchema>) => api.post('/auth/change-password', { currentPassword: v.currentPassword, newPassword: v.newPassword }),
    onSuccess: () => { toast.success('Password changed. Other devices have been signed out.'); form.reset(); },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'incorrect_password') form.setError('currentPassword', { message: 'Current password is incorrect' });
      else setError(applyServerErrors(form as never, e));
    },
  });
  return (
    <Card className="h-100">
      <Card.Header className="fw-semibold">Change password</Card.Header>
      <Card.Body>
        <Form noValidate onSubmit={form.handleSubmit((v) => { setError(null); change.mutate(v); })}>
          <FormError message={error} />
          <TextField form={form as never} name="currentPassword" label="Current password" type="password" autoComplete="current-password" required />
          <TextField form={form as never} name="newPassword" label="New password" type="password" autoComplete="new-password" required help="8+ characters with a letter and a digit" />
          <TextField form={form as never} name="confirm" label="Repeat new password" type="password" autoComplete="new-password" required />
          <Button type="submit" disabled={change.isPending}>{change.isPending ? 'Changing…' : 'Change password'}</Button>
        </Form>
      </Card.Body>
    </Card>
  );
}

export function ProfilePage() {
  return (
    <>
      <PageHeader title="Profile & password" icon="person-circle" />
      <BRow className="g-3">
        <Col lg={6}><DetailsCard /></Col>
        <Col lg={6}><PasswordCard /></Col>
      </BRow>
    </>
  );
}
